import React, { useEffect, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, Plus, Trash2, Upload, X, Link2, Loader2 } from "lucide-react";

const BTN = "font-mono uppercase text-[11px] tracking-wide px-3 py-2 transition-colors";
const EMPTY = { collaborator_id: null, name: "", logo_url: "", link_url: "" };

function CollaboratorForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || { ...EMPTY });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const isEdit = !!form.collaborator_id;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large (max 10MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/collaborators/upload-logo", fd);
      setForm((f) => ({ ...f, logo_url: data.url }));
      toast.success("Logo uploaded.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Upload failed.");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Enter a collaborator name."); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), logo_url: form.logo_url || "", link_url: form.link_url || "" };
      if (isEdit) await api.put(`/admin/collaborators/${form.collaborator_id}`, payload);
      else await api.post("/admin/collaborators", payload);
      toast.success(isEdit ? "Collaborator updated." : "Collaborator added.");
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-sniper bg-surface p-4 space-y-4" data-testid="collaborator-form">
      <div className="flex items-center justify-between">
        <h3 className="font-heading uppercase tracking-tight text-lg">{isEdit ? "Edit collaborator" : "Add collaborator"}</h3>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground" data-testid="collaborator-form-cancel"><X className="w-4 h-4" /></button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        {/* logo */}
        <div className="shrink-0">
          <label className="block font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Logo</label>
          <div className="w-28 h-28 border border-sniper bg-obsidian flex items-center justify-center overflow-hidden relative">
            {form.logo_url
              ? <img src={form.logo_url} alt="" className="max-w-full max-h-full object-contain p-2" data-testid="collaborator-logo-preview" />
              : <span className="font-mono text-[9px] uppercase text-muted-foreground text-center px-2">No logo</span>}
          </div>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className={`${BTN} mt-2 w-28 border border-sniper text-secondary-foreground hover:border-olive-bright flex items-center justify-center gap-1.5`}
            data-testid="collaborator-logo-upload-btn">
            {uploading ? <><Loader2 className="w-3 h-3 animate-spin" /> …</> : <><Upload className="w-3 h-3" /> {form.logo_url ? "Replace" : "Upload"}</>}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="collaborator-logo-input" />
        </div>

        {/* fields */}
        <div className="flex-1 space-y-3">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Name</label>
            <input value={form.name} onChange={set("name")} placeholder="e.g. ELEV850"
              className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-body"
              data-testid="collaborator-name-input" />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Link URL (optional)</label>
            <input value={form.link_url} onChange={set("link_url")} placeholder="https://…"
              className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-body"
              data-testid="collaborator-link-input" />
            <p className="mt-1 font-mono text-[10px] text-muted-foreground">If set, the tile becomes a clickable link to the collaborator.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={save} disabled={saving} className={`${BTN} bg-olive text-white hover:bg-olive-bright disabled:opacity-50`} data-testid="collaborator-save">
          {saving ? "Saving…" : isEdit ? "Save changes" : "Add collaborator"}
        </button>
        <button onClick={onCancel} className={`${BTN} border border-sniper text-muted-foreground hover:text-foreground`}>Cancel</button>
      </div>
    </div>
  );
}

export default function AdminCollaborators() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // collaborator object, {} for new, or null

  const load = async () => {
    try { setRows((await api.get("/admin/collaborators")).data.collaborators || []); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Remove "${c.name}" from the homepage collaborators?`)) return;
    try {
      await api.delete(`/admin/collaborators/${c.collaborator_id}`);
      toast.success("Collaborator removed.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const onSaved = () => { setEditing(null); load(); };

  if (rows === null) return <div className="p-6 font-mono text-xs uppercase text-muted-foreground">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 max-w-[1000px]" data-testid="admin-collaborators">
      <div className="flex items-center gap-3 mb-1">
        <Handshake className="w-5 h-5 text-olive-bright" />
        <h1 className="font-heading text-2xl uppercase tracking-tight">Collaborators</h1>
      </div>
      <p className="text-sm text-secondary-foreground font-body mb-5">
        Brand, creator and outfitter partners shown in the homepage <b className="text-foreground">Collaborators</b> logo grid.
        Only entries <b className="text-foreground">with a logo</b> are rendered — no placeholders. Approving a Partner
        Application drops the partner here as a <b className="text-foreground">draft</b>; add a logo to publish it live.
      </p>

      {!editing && (
        <div className="flex items-center gap-2 mb-4">
          <button onClick={() => setEditing({})} className={`${BTN} bg-olive text-white hover:bg-olive-bright`} data-testid="collaborator-new">
            <Plus className="w-3 h-3 inline mr-1 -mt-0.5" /> Add collaborator
          </button>
          <span className="font-mono text-[10px] uppercase text-muted-foreground ml-2">{rows.length} collaborator{rows.length === 1 ? "" : "s"}</span>
        </div>
      )}

      {editing && (
        <div className="mb-6">
          <CollaboratorForm initial={editing.collaborator_id ? editing : { ...EMPTY }} onSaved={onSaved} onCancel={() => setEditing(null)} />
        </div>
      )}

      <div className="space-y-2" data-testid="collaborator-list">
        {rows.length === 0 && (
          <div className="border border-dashed border-sniper p-8 text-center font-mono text-xs uppercase text-muted-foreground" data-testid="collaborator-empty">
            No collaborators yet — add one to populate the homepage grid.
          </div>
        )}
        {rows.map((c) => (
          <div key={c.collaborator_id} data-testid={`collaborator-row-${c.collaborator_id}`}
            className="border border-sniper bg-surface flex items-center gap-3 p-3">
            <div className="w-14 h-14 bg-obsidian border border-sniper overflow-hidden shrink-0 flex items-center justify-center">
              {c.logo_url
                ? <img src={c.logo_url} alt="" className="max-w-full max-h-full object-contain p-1.5" />
                : <span className="font-mono text-[8px] uppercase text-muted-foreground">No logo</span>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="font-heading uppercase tracking-tight text-foreground truncate">{c.name}</div>
                {!(c.logo_url || "").trim() && (
                  <span className="shrink-0 font-mono text-[9px] uppercase tracking-wide px-1.5 py-0.5 border border-yellow-500/50 text-yellow-400" data-testid={`collaborator-draft-${c.collaborator_id}`} title="Not shown on /partners until a logo is added">
                    Draft · needs logo
                  </span>
                )}
                {c.source === "partner_application" && (
                  <span className="shrink-0 font-mono text-[9px] uppercase tracking-wide px-1.5 py-0.5 border border-sniper text-muted-foreground">
                    From application
                  </span>
                )}
              </div>
              {c.link_url
                ? <a href={c.link_url} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-olive-bright hover:underline inline-flex items-center gap-1 mt-0.5 truncate max-w-full">
                    <Link2 className="w-3 h-3 shrink-0" /> <span className="truncate">{c.link_url}</span>
                  </a>
                : <span className="font-mono text-[10px] uppercase text-muted-foreground mt-0.5 block">No link</span>}
            </div>
            <button onClick={() => setEditing(c)} className={`${BTN} border border-sniper text-secondary-foreground hover:border-olive-bright shrink-0`} data-testid={`collaborator-edit-${c.collaborator_id}`}>Edit</button>
            <button onClick={() => remove(c)} className={`${BTN} border border-blaze/50 text-blaze hover:bg-blaze/10 shrink-0 flex items-center gap-1`} data-testid={`collaborator-remove-${c.collaborator_id}`}>
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
